import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import { formatCurrency, parseCurrencyInput } from '../utils/currency';
import type { Slot } from '../types';

interface SlotPriceOverrideModalProps {
  slot: Slot;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedSlot: Slot) => void;
}

/**
 * Slot Price Override Modal Component
 * Issue #966 - Phase 1: Slot Price Override
 *
 * Modal component for setting individual slot price overrides.
 * Allows Fair Managers to set premium pricing or discounts for specific slots.
 */
export default function SlotPriceOverrideModal({
  slot,
  isOpen,
  onClose,
  onSuccess
}: SlotPriceOverrideModalProps) {
  const [priceInput, setPriceInput] = useState('');
  const [useOverride, setUseOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Initialize form with current override value if exists
      if (slot.price_override) {
        setUseOverride(true);
        setPriceInput(slot.price_override);
      } else {
        setUseOverride(false);
        setPriceInput(slot.base_price || '0');
      }
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, slot]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate raw input before parsing
      if (useOverride) {
        // Check if input contains at least some numeric characters
        const hasNumbers = /\d/.test(priceInput);
        if (!hasNumbers || priceInput.trim() === '') {
          setError('Please enter a valid price');
          setSaving(false);
          return;
        }

        // Validate the parsed numeric value
        const testValue = parseFloat(priceInput.replace(/[^0-9.-]/g, ''));
        if (isNaN(testValue) || testValue < 0) {
          setError('Please enter a valid price');
          setSaving(false);
          return;
        }
      }

      // Determine price override value (after validation)
      const priceOverride = useOverride ? parseCurrencyInput(priceInput) : null;

      // Call API to set override (pass null to clear)
      const updatedSlot = await fairManagerApi.setSlotPriceOverride(slot.id, priceOverride);

      setSuccess(true);
      setTimeout(() => {
        onSuccess(updatedSlot);
        onClose();
      }, 1500);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const basePrice = parseFloat(slot.base_price || '0');
  const overridePrice = useOverride ? parseFloat(parseCurrencyInput(priceInput) || '0') : null;
  const priceDifference = overridePrice !== null ? overridePrice - basePrice : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Set Slot Price Override</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-green-800 font-medium">Price override updated successfully!</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Slot Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Slot Information</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Slot Number:</span>
                <span className="font-medium text-gray-900">{slot.slot_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Base Price:</span>
                <span className="font-medium text-gray-900">{formatCurrency(slot.base_price)}</span>
              </div>
              {slot.price_override && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Override:</span>
                  <span className="font-medium text-secondary">{formatCurrency(slot.price_override)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Override Toggle */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="useOverride"
              checked={useOverride}
              onChange={(e) => setUseOverride(e.target.checked)}
              className="mt-1 h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
              data-testid="use-override-checkbox"
            />
            <label htmlFor="useOverride" className="ml-3">
              <span className="text-sm font-medium text-gray-900 block">Use Custom Price</span>
              <span className="text-sm text-gray-600">
                Override the base price for this specific slot
              </span>
            </label>
          </div>

          {/* Price Input */}
          {useOverride && (
            <div>
              <label htmlFor="priceOverride" className="block text-sm font-medium text-gray-700 mb-2">
                Override Price
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="priceOverride"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
                  placeholder="0.00"
                  data-testid="price-override-input"
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Enter the custom price for this slot
              </p>
            </div>
          )}

          {/* Price Preview */}
          {useOverride && (
            <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
              <h4 className="font-semibold text-ink mb-2">Price Preview</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-primary-dark">Base Price:</span>
                  <span className="font-medium text-ink">{formatCurrency(slot.base_price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-dark">New Price:</span>
                  <span className="font-medium text-ink">{formatCurrency(priceInput || '0')}</span>
                </div>
                <div className="flex justify-between border-t border-teal-300 pt-2">
                  <span className="text-primary-dark">Difference:</span>
                  <span className={`font-medium ${priceDifference > 0 ? 'text-green-600' : priceDifference < 0 ? 'text-red-600' : 'text-ink'}`}>
                    {priceDifference > 0 ? '+' : ''}{formatCurrency(Math.abs(priceDifference).toString())}
                    {priceDifference > 0 && ' (premium)'}
                    {priceDifference < 0 && ' (discount)'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Info Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-900 font-medium">Price Override Guidelines</p>
                <ul className="text-sm text-yellow-800 mt-2 space-y-1 list-disc list-inside">
                  <li>Use premium pricing for high-traffic or corner booths</li>
                  <li>Offer discounts for less desirable locations</li>
                  <li>Price changes apply immediately to available slots</li>
                  <li>Existing reservations are not affected</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-4 p-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving || success}
            className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            data-testid="save-price-override-button"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : success ? (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Saved!
              </>
            ) : (
              <>
                <DollarSign className="h-5 w-5 mr-2" />
                Save Price Override
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
